// Bureau Portal Route Configuration
import { lazy } from 'react';

// Lazy load Bureau Portal components
const BureauDashboard = lazy(() => import('../../portals/bureau/pages/dashboard/BureauDashboard'));
const AlertsManagement = lazy(() => import('../../portals/bureau/pages/alerts/AlertsManagement'));
const EventsManagement = lazy(() => import('../../portals/bureau/pages/events/EventsManagement'));
const SuppressionManagement = lazy(
  () => import('../../portals/bureau/pages/suppression/SuppressionManagement')
);
const MonitoringTools = lazy(() => import('../../portals/bureau/pages/monitoring/MonitoringTools'));
const TestingInterface = lazy(() => import('../../portals/bureau/pages/testing/TestingInterface'));
const BureauSettings = lazy(() => import('../../portals/bureau/pages/settings/BureauSettings'));
const TaskDashboard = lazy(() => import('../../portals/bureau/pages/tasks/TaskDashboard'));
const MySupportTickets = lazy(() => import('../../portals/bureau/pages/support/MySupportTickets'));
const Notifications = lazy(() => import('../../portals/bureau/pages/notifications/Notifications'));
const TestingGuide = lazy(() => import('../../portals/bureau/pages/help/TestingGuide'));
const MySupportPage = lazy(() => import('../../shared/support/pages/MySupportPage'));

/**
 * Bureau Portal Routes Configuration
 * For Bureau staff, agents, and operational users
 */
export const bureauRoutes = {
  path: '/bureau',
  name: 'Bureau Portal',
  description: 'Portal for Bureau staff, agents, and operational users',
  requiredRoles: [], // No hardcoded roles required - portal access is determined by assigned_portal field
  requiredPermissions: [], // No hardcoded permissions required
  children: [
    {
      index: true,
      component: BureauDashboard,
      name: 'Dashboard',
      description: 'Bureau operations dashboard',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'Bureau Dashboard',
        breadcrumb: 'Dashboard',
        icon: 'dashboard',
      },
    },
    {
      path: 'alerts',
      component: AlertsManagement,
      name: 'Alerts Management',
      description: 'Manage and monitor alerts',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'Alerts Management',
        breadcrumb: 'Alerts',
        icon: 'warning',
      },
      children: [
        {
          path: ':alertId',
          component: lazy(() => import('../../portals/bureau/pages/alerts/AlertDetail')),
          name: 'Alert Detail',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Alert Details',
            breadcrumb: 'Alert Details',
            hideInNavigation: true,
          },
        },
        {
          path: 'create',
          component: lazy(() => import('../../portals/bureau/pages/alerts/CreateAlert')),
          name: 'Create Alert',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Create Alert',
            breadcrumb: 'Create Alert',
            hideInNavigation: true,
          },
        },
      ],
    },
    {
      path: 'events',
      component: EventsManagement,
      name: 'Events Management',
      description: 'Manage and track events',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'Events Management',
        breadcrumb: 'Events',
        icon: 'event',
      },
      children: [
        {
          path: ':eventId',
          component: lazy(() => import('../../portals/bureau/pages/events/EventDetail')),
          name: 'Event Detail',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Event Details',
            breadcrumb: 'Event Details',
            hideInNavigation: true,
          },
        },
        {
          path: 'create',
          component: lazy(() => import('../../portals/bureau/pages/events/CreateEvent')),
          name: 'Create Event',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Create Event',
            breadcrumb: 'Create Event',
            hideInNavigation: true,
          },
        },
      ],
    },
    {
      path: 'suppression',
      component: SuppressionManagement,
      name: 'Suppression Management',
      description: 'Manage alert suppression rules',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'Suppression Management',
        breadcrumb: 'Suppression',
        icon: 'block',
      },
      children: [
        {
          path: 'profiles',
          component: lazy(
            () => import('../../portals/bureau/pages/suppression/SuppressionProfiles')
          ),
          name: 'Suppression Profiles',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Suppression Profiles',
            breadcrumb: 'Profiles',
          },
        },
        {
          path: 'overrides',
          component: lazy(() => import('../../portals/bureau/pages/suppression/DeviceOverrides')),
          name: 'Device Overrides',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Device Overrides',
            breadcrumb: 'Overrides',
          },
        },
        {
          path: 'analytics',
          component: lazy(
            () => import('../../portals/bureau/pages/suppression/SuppressionAnalytics')
          ),
          name: 'Suppression Analytics',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Suppression Analytics',
            breadcrumb: 'Analytics',
          },
        },
      ],
    },
    {
      path: 'monitoring',
      component: MonitoringTools,
      name: 'Monitoring Tools',
      description: 'Real-time monitoring and diagnostics',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'Monitoring Tools',
        breadcrumb: 'Monitoring',
        icon: 'monitor',
      },
      children: [
        {
          path: 'realtime',
          component: lazy(() => import('../../portals/bureau/pages/monitoring/RealtimeMonitoring')),
          name: 'Real-time Monitoring',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Real-time Monitoring',
            breadcrumb: 'Real-time',
          },
        },
        {
          path: 'diagnostics',
          component: lazy(() => import('../../portals/bureau/components/SystemDiagnostics')),
          name: 'System Diagnostics',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'System Diagnostics',
            breadcrumb: 'Diagnostics',
          },
        },
      ],
    },
    {
      path: 'testing',
      component: TestingInterface,
      name: 'Testing Interface',
      description: 'Testing and validation tools',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'Testing Interface',
        breadcrumb: 'Testing',
        icon: 'science',
      },
      children: [
        {
          path: 'alerts',
          component: lazy(() => import('../../portals/bureau/components/AlertTesting')),
          name: 'Alert Testing',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Alert Testing',
            breadcrumb: 'Alert Tests',
          },
        },
        {
          path: 'devices',
          component: lazy(() => import('../../portals/bureau/components/DeviceTesting')),
          name: 'Device Testing',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Device Testing',
            breadcrumb: 'Device Tests',
          },
        },
      ],
    },
    {
      path: 'tasks',
      component: TaskDashboard,
      name: 'Tasks',
      description: 'Manage and track your assignments',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'Tasks',
        breadcrumb: 'Tasks',
        icon: 'assignment',
      },
    },
    {
      path: 'support',
      component: MySupportTickets,
      name: 'Support',
      description: 'View and manage support tickets',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'My Support Tickets',
        breadcrumb: 'Support',
        icon: 'support',
      },
    },
    {
      path: 'notifications',
      component: Notifications,
      name: 'Notifications',
      description: 'View all notifications',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'Notifications',
        breadcrumb: 'Notifications',
        icon: 'notifications',
      },
    },
    {
      path: 'settings',
      component: BureauSettings,
      name: 'Settings',
      description: 'Customize your Bureau experience',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'Settings',
        breadcrumb: 'Settings',
        icon: 'settings',
      },
    },
    {
      path: 'my-support',
      component: MySupportPage,
      name: 'My Support',
      description: 'My tickets and notifications',
      requiredPermissions: [],
      meta: {
        title: 'My Support',
        breadcrumb: 'Support',
        icon: 'support_agent',
      },
    },
    {
      path: 'help/testing-guide',
      component: TestingGuide,
      name: 'Testing Guide',
      description: 'Guide for testing workflows and CMS integration',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'Testing Guide',
        breadcrumb: 'Testing Guide',
        icon: 'help',
      },
    },
  ],
};

export default bureauRoutes;
